import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { qk } from '@/lib/query';
import * as api from '@/api/misc';
import type { CreditsFeesQuery, CreditFeeInput } from '@/api/misc';
import { useOptimisticMutation, patchPaged, prependPaged, removeById, replaceById } from './optimistic';
import { tempId } from '@/lib/utils';
import type { CreditOrFee, Paginated } from '@/types';

type Cache = Paginated<CreditOrFee>;

export function useCreditsAndFees(query: CreditsFeesQuery) {
  return useQuery({
    queryKey: [...qk.creditsAndFeesRoot, query] as const,
    queryFn: ({ signal }) => api.fetchCreditsAndFees(query, signal),
    placeholderData: keepPreviousData
  });
}

export function useCreateCreditOrFee(clientName: (id: string) => string) {
  return useOptimisticMutation<Cache, CreditFeeInput, CreditOrFee>({
    mutationFn: api.createCreditOrFee,
    errorMessage: 'That line could not be saved.',
    optimistic: () => ({
      patches: [
        {
          key: qk.creditsAndFeesRoot,
          update: (cache: never, vars) =>
            prependPaged(cache as Cache, {
              id: tempId('cf'),
              clientId: vars.clientId,
              clientName: vars.clientId ? clientName(vars.clientId) : null,
              invoiceId: null,
              kind: vars.kind,
              description: vars.description,
              amount: vars.amount,
              isHourly: vars.isHourly,
              date: vars.date,
              applied: false
            }) as never
        }
      ],
      invalidate: [qk.creditsAndFeesRoot]
    }),
    onSuccessSideEffect(created, _vars, qc) {
      qc.setQueriesData<Cache>({ queryKey: qk.creditsAndFeesRoot }, (cache) =>
        cache
          ? { ...cache, items: cache.items.map((i) => (i.id.startsWith('tmp_') ? created : i)) }
          : cache
      );
    }
  });
}

export function useUpdateCreditOrFee() {
  return useOptimisticMutation<Cache, { id: string; input: CreditFeeInput }, CreditOrFee>({
    mutationFn: ({ id, input }) => api.updateCreditOrFee(id, input),
    errorMessage: 'That change could not be saved.',
    optimistic: () => ({
      patches: [
        {
          key: qk.creditsAndFeesRoot,
          update: (cache: never, vars) =>
            patchPaged(cache as Cache, (items) =>
              replaceById(items, vars.id, {
                kind: vars.input.kind,
                description: vars.input.description,
                amount: vars.input.amount,
                date: vars.input.date,
                clientId: vars.input.clientId
              })
            ) as never
        }
      ],
      invalidate: [qk.creditsAndFeesRoot]
    })
  });
}

export function useDeleteCreditOrFee() {
  return useOptimisticMutation<Cache, { id: string }, unknown>({
    mutationFn: ({ id }) => api.deleteCreditOrFee(id),
    errorMessage: 'That line could not be deleted.',
    optimistic: () => ({
      patches: [
        {
          key: qk.creditsAndFeesRoot,
          update: (cache: never, vars) =>
            patchPaged(cache as Cache, (items) => removeById(items, vars.id)) as never
        }
      ],
      invalidate: [qk.creditsAndFeesRoot]
    }),
    successMessage: 'Deleted'
  });
}
